import {
  useEffect,
  useState,
} from "react";

import {
  Navigate,
  useNavigate,
  useParams,
} from "react-router-dom";

import axios from "axios";

import { useAuth } from "@/features/auth/hooks/useAuth";

import {
  acceptInvitation,
  validateInvitation,
} from "../api/onboardingApi";

import { CreatePasswordForm } from "../components/CreatePasswordForm";

import type {
  AcceptInvitationResponse,
  InvitationValidationResponse,
} from "../types/onboarding";

type InvitationErrorType =
  | "EXPIRED"
  | "ACCEPTED"
  | "REVOKED"
  | "NOT_FOUND"
  | "INVALID";

type InvitationErrorState = {
  type: InvitationErrorType;
  message: string;
};

function getApiErrorDetail(
  error: unknown,
): string | null {
  if (!axios.isAxiosError(error)) {
    return null;
  }

  const detail = error.response?.data?.detail;

  return typeof detail === "string"
    ? detail
    : null;
}

function classifyInvitationError(
  error: unknown,
): InvitationErrorState {
  if (!axios.isAxiosError(error)) {
    return {
      type: "INVALID",
      message:
        "This invitation could not be validated.",
    };
  }

  const statusCode = error.response?.status;
  const detail =
    getApiErrorDetail(error) ?? "";

  const normalizedDetail = detail.toLowerCase();

  if (
    statusCode === 410 &&
    normalizedDetail.includes("expired")
  ) {
    return {
      type: "EXPIRED",
      message:
        "This invitation has expired and can no longer be used.",
    };
  }

  if (
    statusCode === 410 &&
    normalizedDetail.includes("revoked")
  ) {
    return {
      type: "REVOKED",
      message:
        "This invitation has been revoked and can no longer be used.",
    };
  }

  if (
    statusCode === 409 &&
    (
      normalizedDetail.includes("accepted") ||
      normalizedDetail.includes(
        "already active",
      ) ||
      normalizedDetail.includes(
        "already been activated",
      )
    )
  ) {
    return {
      type: "ACCEPTED",
      message:
        "This invitation has already been accepted.",
    };
  }

  if (statusCode === 404) {
    return {
      type: "NOT_FOUND",
      message:
        "We could not find an invitation matching this link.",
    };
  }

  return {
    type: "INVALID",
    message:
      detail ||
      "This invitation is no longer available.",
  };
}

function getSubmissionErrorMessage(
  error: unknown,
): string {
  const detail = getApiErrorDetail(error);

  if (detail) {
    return detail;
  }

  return "Your account could not be created. Please try again.";
}

function InvitationLoadingState() {
  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="text-center">
        <div className="mx-auto h-11 w-11 animate-spin rounded-full border-4 border-slate-200 border-t-emerald-600" />

        <h1 className="mt-5 text-lg font-semibold text-slate-900">
          Preparing your workspace
        </h1>

        <p className="mt-2 text-sm text-slate-600">
          We are validating your invitation.
        </p>
      </div>
    </main>
  );
}

function InvitationErrorPage({
  error,
}: {
  error: InvitationErrorState;
}) {
  const content = {
    EXPIRED: {
      title: "Invitation expired",
      actionLabel: "Request new access",
      actionHref: "/request-access",
      icon: "?",
    },
    ACCEPTED: {
      title: "Workspace already activated",
      actionLabel: "Go to login",
      actionHref: "/login",
      icon: "?",
    },
    REVOKED: {
      title: "Invitation revoked",
      actionLabel: "Request new access",
      actionHref: "/request-access",
      icon: "!",
    },
    NOT_FOUND: {
      title: "Invitation not found",
      actionLabel: "Go to login",
      actionHref: "/login",
      icon: "?",
    },
    INVALID: {
      title: "Invitation unavailable",
      actionLabel: "Go to login",
      actionHref: "/login",
      icon: "!",
    },
  }[error.type];

  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <section className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-xl shadow-slate-200/60">
        <p className="text-sm font-bold uppercase tracking-[0.2em] text-emerald-600">
          MinistryIQ
        </p>

        <div className="mx-auto mt-6 flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-xl font-bold text-slate-700">
          {content.icon}
        </div>

        <h1 className="mt-5 text-2xl font-bold text-slate-950">
          {content.title}
        </h1>

        <p className="mt-3 leading-6 text-slate-600">
          {error.message}
        </p>

        {error.type === "REVOKED" && (
          <p className="mt-3 text-sm text-slate-500">
            Contact the MinistryIQ administrator who
            originally invited you if you believe this
            was a mistake.
          </p>
        )}

        <a
          href={content.actionHref}
          className="mt-7 inline-flex w-full items-center justify-center rounded-xl bg-slate-950 px-5 py-3 font-semibold text-white transition hover:bg-slate-800"
        >
          {content.actionLabel}
        </a>
      </section>
    </main>
  );
}

function ActivationSuccessPage({
  result,
}: {
  result: AcceptInvitationResponse;
}) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-950 px-4">
      <section className="w-full max-w-md rounded-3xl border border-slate-800 bg-slate-900 p-8 text-center shadow-2xl">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500 text-3xl font-bold text-white">
          ?
        </div>

        <p className="mt-6 text-sm font-bold uppercase tracking-[0.2em] text-emerald-400">
          MinistryIQ
        </p>

        <h1 className="mt-3 text-3xl font-bold text-white">
          Workspace activated
        </h1>

        <p className="mt-4 text-slate-300">
          Welcome to{" "}
          <span className="font-semibold text-white">
            {result.church.name}
          </span>
          .
        </p>

        <div className="mt-6 rounded-2xl border border-slate-700 bg-slate-950/50 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Administrator
          </p>

          <p className="mt-1 break-all font-medium text-slate-200">
            {result.user.email}
          </p>
        </div>

        <div className="mt-7 flex items-center justify-center gap-3 text-sm text-slate-400">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-slate-600 border-t-emerald-400" />

          Opening your dashboard...
        </div>
      </section>
    </main>
  );
}

export function InvitationPage() {
  const { token } = useParams<{
    token: string;
  }>();

  const navigate = useNavigate();

  const {
    isAuthenticated,
    completeInvitationActivation,
  } = useAuth();

  const [
    invitation,
    setInvitation,
  ] = useState<InvitationValidationResponse | null>(
    null,
  );

  const [
    activationResult,
    setActivationResult,
  ] = useState<AcceptInvitationResponse | null>(
    null,
  );

  const [isLoading, setIsLoading] = useState(true);

  const [isSubmitting, setIsSubmitting] =
    useState(false);

  const [
    validationError,
    setValidationError,
  ] = useState<InvitationErrorState | null>(
    null,
  );

  const [submissionError, setSubmissionError] =
    useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function loadInvitation() {
      if (!token) {
        setValidationError({
          type: "INVALID",
          message:
            "The invitation link is missing its security token.",
        });

        setIsLoading(false);
        return;
      }

      try {
        const result = await validateInvitation(token);

        if (isMounted) {
          setInvitation(result);
        }
      } catch (error) {
        if (isMounted) {
          setValidationError(
            classifyInvitationError(error),
          );
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadInvitation();

    return () => {
      isMounted = false;
    };
  }, [token]);

  useEffect(() => {
    if (!activationResult) {
      return;
    }

    const redirectTimer = window.setTimeout(() => {
      navigate("/", {
        replace: true,
      });
    }, 1400);

    return () => {
      window.clearTimeout(redirectTimer);
    };
  }, [activationResult, navigate]);

  async function handleAcceptInvitation(
    password: string,
  ) {
    if (!token) {
      setSubmissionError(
        "The invitation link is invalid.",
      );
      return;
    }

    setSubmissionError(null);
    setIsSubmitting(true);

    try {
      const result = await acceptInvitation(
        token,
        {
          password,
        },
      );

      setActivationResult(result);

      completeInvitationActivation(result);
    } catch (error) {
      setSubmissionError(
        getSubmissionErrorMessage(error),
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  if (activationResult) {
    return (
      <ActivationSuccessPage
        result={activationResult}
      />
    );
  }

  if (isAuthenticated) {
    return (
      <Navigate
        to="/"
        replace
      />
    );
  }

  if (isLoading) {
    return <InvitationLoadingState />;
  }

  if (validationError || !invitation) {
    return (
      <InvitationErrorPage
        error={
          validationError ?? {
            type: "INVALID",
            message:
              "This invitation is not available.",
          }
        }
      />
    );
  }

  const invitedName = [
    invitation.invited_first_name,
    invitation.invited_last_name,
  ]
    .filter(Boolean)
    .join(" ");

  const expirationDate = new Date(
    invitation.expires_at,
  );

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-12">
      <div className="mx-auto w-full max-w-lg">
        <header className="mb-8 text-center">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-emerald-600">
            MinistryIQ
          </p>

          <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-950">
            Welcome to MinistryIQ
          </h1>

          <p className="mt-3 text-slate-600">
            Create your administrator account to
            activate your church workspace.
          </p>
        </header>

        <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl shadow-slate-200/60">
          <div className="border-b border-slate-800 bg-slate-950 px-8 py-7 text-white">
            <p className="text-sm font-medium text-emerald-300">
              You have been invited to manage
            </p>

            <h2 className="mt-2 text-2xl font-bold">
              {invitation.church_name}
            </h2>

            <p className="mt-2 text-sm text-slate-400">
              MinistryIQ pilot workspace
            </p>
          </div>

          <div className="space-y-7 p-8">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
              <dl className="space-y-4">
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Administrator
                  </dt>

                  <dd className="mt-1 font-medium text-slate-900">
                    {invitedName ||
                      "Church Administrator"}
                  </dd>
                </div>

                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Email
                  </dt>

                  <dd className="mt-1 break-all font-medium text-slate-900">
                    {invitation.invited_email}
                  </dd>
                </div>

                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Role
                  </dt>

                  <dd className="mt-1">
                    <span className="inline-flex rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                      {invitation.role_code}
                    </span>
                  </dd>
                </div>
              </dl>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-slate-950">
                Create your password
              </h3>

              <p className="mt-1 text-sm leading-6 text-slate-500">
                This password will be used to sign
                in to your MinistryIQ workspace.
              </p>
            </div>

            <CreatePasswordForm
              isSubmitting={isSubmitting}
              errorMessage={submissionError}
              onSubmit={handleAcceptInvitation}
            />
          </div>
        </section>

        <p className="mt-6 text-center text-xs text-slate-500">
          This secure invitation expires on{" "}
          {expirationDate.toLocaleDateString(
            undefined,
            {
              year: "numeric",
              month: "long",
              day: "numeric",
            },
          )}
          .
        </p>
      </div>
    </main>
  );
}

